import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { UploadWhiteLabelBase64ImagesDTO, WhiteLabelService } from '@api/service/white-label.service';
import { WhiteLabel, WhiteLabelTextReplacement } from '@api/model/white-label';
import { ActivatedRoute, Router } from '@angular/router';
import { emailValidator } from '@api/util/validators';
import { distinctUntilChanged, filter, map, switchMap, takeUntil, timeout } from 'rxjs/operators';
import { noop, Observable, of, Subject } from 'rxjs';
import { formatPhone } from '@api/utils';
import { jsonValidator } from '@validators/json.validator';
import { xmlValidator } from '@validators/xml.validator';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ClipboardService } from 'ngx-clipboard';
import { NzUploadChangeParam } from 'ng-zorro-antd/upload';
import { OneSignalApp, OneSignalService } from '@api/service/one-signal.service';
import { environment } from '@environment';
import { formatCnpj } from '@api/util/formatters';
import { isValidCNPJ } from '@api/utils';

import {
  CreateWhitelabelIconAndImagesChangeFormState,
  WHITELABEL_IMAGES,
  WhitelabelImages
} from './components/create-white-label-icon-and-images/create-white-label-icon-and-images.component';

const awsS3Paths: any = {
  'resources/icon.png': 'icon.png',
  'resources/splash.png': 'splash.png',
  'src/assets/img/logo.png': 'logo.png',
  'resources/custom/android-notifications/drawable-hdpi/ic_stat_onesignal_default.png':
    'notification-icons/drawable-hdpi/ic_stat_onesignal_default.png',
  'resources/custom/android-notifications/drawable-mdpi/ic_stat_onesignal_default.png':
    'notification-icons/drawable-mdpi/ic_stat_onesignal_default.png',
  'resources/custom/android-notifications/drawable-xhdpi/ic_stat_onesignal_default.png':
    'notification-icons/drawable-xhdpi/ic_stat_onesignal_default.png',
  'resources/custom/android-notifications/drawable-xxhdpi/ic_stat_onesignal_default.png':
    'notification-icons/drawable-xxhdpi/ic_stat_onesignal_default.png',
  'resources/custom/android-notifications/drawable-xxxhdpi/ic_stat_onesignal_default.png':
    'notification-icons/drawable-xxxhdpi/ic_stat_onesignal_default.png',
  'resources/custom/android-notifications/drawable-xxxhdpi/ic_onesignal_large_icon_default.png':
    'notification-icons/drawable-xxxhdpi/ic_onesignal_large_icon_default.png'
};

const validIfEmptyEmailValidator = (c: AbstractControl) => {
  if (!c.value) return;
  return emailValidator(c);
};

const getBase64FromFile = (file: File): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

type ResourceReplacements = {
  icon?: string | null;
  splash?: string | null;
  logo?: string | null;
  banner?: string | null;
  favIcon?: string | null;
};

type WhitelabelAppsForm = {
  hasWebApp: FormControl<boolean | undefined>;
  hasMobileApp: FormControl<boolean | undefined>;
  productBannersEnabled: FormControl<boolean | undefined>;
};

@Component({
  selector: 'app-create-white-label-page',
  templateUrl: './create-white-label-page.html',
  styleUrls: ['./create-white-label-page.scss']
})
export class CreateWhiteLabelComponent implements OnInit, OnDestroy {
  @Input() public whitelabel!: WhiteLabel;

  public whitelabelDetailsForm!: UntypedFormGroup;
  // public whitelabelResourcesForm!: FormGroup;
  public whitelabelStoreDetailsForm!: UntypedFormGroup;
  public whitelabelStylesForm!: UntypedFormGroup;
  public whitelabelIntegrationKeysForm!: UntypedFormGroup;
  public whitelabelFcmConfigForm!: UntypedFormGroup;
  // public whitelabelTextReplacementsForm!: FormArray;
  // public whitelabelFileReplacementsForm!: FormArray;
  public whitelabelEmailConfigForm!: UntypedFormGroup;
  public whitelabelAccessPermissionForm!: UntypedFormGroup;

  public whitelabelAppsForm = new FormGroup<WhitelabelAppsForm>({
    hasWebApp: new FormControl(undefined, { nonNullable: true }),
    hasMobileApp: new FormControl(undefined, { nonNullable: true }),
    productBannersEnabled: new FormControl(undefined, { nonNullable: true })
  });

  public whitelabelImagesFormState: CreateWhitelabelIconAndImagesChangeFormState = {
    valid: true,
    invalid: false,
    touched: false
  };

  public whitelabelImages: WhitelabelImages = WHITELABEL_IMAGES.reduce((acc, cur) => {
    acc[cur] = {
      imageBase64: null,
      originalImageBase64: null,
      imageFileName: null,
      imageFolder: null,
      derivedImages: null,
      isCropped: false
    };

    return acc;
  }, {} as WhitelabelImages);

  public textReplacements: WhiteLabelTextReplacement[] = [];

  public smtpByNodeMailer = false;

  public index = 0;
  public STEPS = {
    DETAIL: 0,
    ICON_AND_IMAGES: 1,
    TECHNICAL_CONFIG: 2
  };
  public STATUS = {
    LOADING: 0,
    SUCCESS: 1,
    ERROR: 2
  };
  public status: number | null = null;

  public oneSignalSetup!: UntypedFormGroup;
  public oneSignalSetupStatus: number | null = null;
  public oneSignalCertificateUpload$!: Observable<string>;

  public fcmFieldsVisibility: Record<string, boolean> = {
    fcmSdkAdminFirebaseFile: false,
    fcmAndroidJsonFile: false,
    fcmIosPlistFile: false,
    fcmWebPushFile: false,
    fcmVapidKey: false
  };

  private destroyed$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly whitelabelService: WhiteLabelService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notificationService: NzNotificationService,
    private clipboard: ClipboardService,
    private oneSignalService: OneSignalService
  ) {
    this.oneSignalCertificateUpload$ = new Observable<string>();
  }

  private setValuesToUpdate(): void {
    const {
      enabled,
      name,
      companyName,
      emailSignature,
      emailFromName,
      emailFrom,
      phone,
      emailSupport,
      webAppUrl,
      qrCodePageUrl,
      qrCodePageMessage,
      pageTitle,
      x86Installer,
      x64Installer,
      applePackageName,
      appleAppId,
      googlePackageName,
      cssVariables,
      oneSignalRestApiKey,
      oneSignalGoogleSenderId,
      oneSignalAppId,
      emailjs,
      customerSupportUrl,
      nodemailerUri,
      textReplacements,
      isPrivate,
      fcmSdkAdminFirebaseFile,
      fcmAndroidJsonFile,
      fcmIosPlistFile,
      fcmWebPushFile,
      fcmVapidKey,
      cnpj,
      gestor,
      hasWebApp,
      hasMobileApp,
      productBannersEnabled
    } = this.whitelabel;

    const welcomeMessageReplacement = (textReplacements || []).find(
      (replacement: any) => replacement?.from === 'Seja bem vindo ao eCondos!'
    );
    const welcomeMessage = welcomeMessageReplacement?.to || '';

    const details = {
      enabled,
      name,
      companyName,
      emailSignature,
      emailFromName,
      emailFrom,
      phone,
      emailSupport,
      webAppUrl,
      qrCodePageUrl,
      qrCodePageMessage,
      pageTitle,
      x86Installer,
      x64Installer,
      customerSupportUrl,
      welcomeMessage,
      cnpj,
      gestor
    };
    const packages = { applePackageName, googlePackageName, appleAppId };
    const integrationKeys = { oneSignalAppId, oneSignalRestApiKey, oneSignalGoogleSenderId };
    const fcmConfig = { fcmSdkAdminFirebaseFile, fcmAndroidJsonFile, fcmIosPlistFile, fcmWebPushFile, fcmVapidKey };
    const permissionAccessKeys = { isPrivate };
    const whitelabelAppsKeys = { hasWebApp, hasMobileApp, productBannersEnabled };

    this.whitelabelDetailsForm.patchValue(details, { emitEvent: false });
    this.whitelabelStoreDetailsForm.patchValue(packages, { emitEvent: false });

    this.whitelabelIntegrationKeysForm.patchValue(integrationKeys, { emitEvent: false });
    this.whitelabelIntegrationKeysForm.markAsDirty();

    this.whitelabelFcmConfigForm.patchValue(fcmConfig, { emitEvent: false });

    // Mostrar campos FCM que não têm dados
    if (!fcmSdkAdminFirebaseFile) {
      this.fcmFieldsVisibility.fcmSdkAdminFirebaseFile = true;
    }
    if (!fcmAndroidJsonFile) {
      this.fcmFieldsVisibility.fcmAndroidJsonFile = true;
    }
    if (!fcmIosPlistFile) {
      this.fcmFieldsVisibility.fcmIosPlistFile = true;
    }
    if (!fcmWebPushFile) {
      this.fcmFieldsVisibility.fcmWebPushFile = true;
    }
    if (!fcmVapidKey) {
      this.fcmFieldsVisibility.fcmVapidKey = true;
    }

    this.whitelabelStylesForm.patchValue(cssVariables, { emitEvent: false });

    this.whitelabelAccessPermissionForm.patchValue(permissionAccessKeys, { emitEvent: false });

    this.whitelabelAppsForm.patchValue(whitelabelAppsKeys, { emitEvent: false });

    if (nodemailerUri?.length) {
      // smtps://app@conecta-mais.com:Conect@+rs0519@mail.conecta-mais.com:465
      const [, email, passwordAndServer, port] = nodemailerUri.replace('//', '').split(':');
      const [password, server] = passwordAndServer.split('@');
      this.whitelabelEmailConfigForm.patchValue({
        user: email,
        password,
        host: server,
        port
      });
      this.smtpByNodeMailer = true;
    } else if (emailjs) {
      this.whitelabelEmailConfigForm.patchValue(emailjs, { emitEvent: false });
      this.smtpByNodeMailer = false;
    }
  }

  private initForms(): void {
    const cnpjValidator = (c: FormControl) => {
      if (!c.value) {
        return null;
      }

      const cnpjClean = c.value.replace(/\D/g, '');

      if (cnpjClean.length !== 14) {
        return { invalidCnpj: { valid: false } };
      }

      if (isValidCNPJ(cnpjClean)) {
        return null;
      }

      return { invalidCnpj: { valid: false } };
    };

    this.whitelabelDetailsForm = this.formBuilder.group({
      enabled: [true],
      name: ['', [Validators.required]],
      companyName: [''],
      emailSignature: [''],
      emailFromName: [''],
      emailFrom: ['', [validIfEmptyEmailValidator]],
      phone: ['', [Validators.maxLength(15), Validators.minLength(14)]],
      emailSupport: ['', [validIfEmptyEmailValidator]],
      webAppUrl: [''],
      qrCodePageUrl: [''],
      qrCodePageMessage: [''],
      pageTitle: [''],
      x86Installer: [''],
      x64Installer: [''],
      customerSupportUrl: [''],
      welcomeMessage: [''],
      cnpj: ['', [cnpjValidator]],
      gestor: ['']
    });
    this.whitelabelStylesForm = this.formBuilder.group({
      '--navbar-background': [''],
      '--navbar-text': [''],
      '--navbar-hover-background': [''],
      '--navbar-hover-text': [''],
      '--modal-header-background': [''],
      '--modal-header-text': [''],
      '--primary': [''],
      '--primary-darker': [''],
      '--on-primary': [''],
      '--background': [''],
      '--on-background': ['']
    });
    this.whitelabelIntegrationKeysForm = this.formBuilder.group({
      oneSignalAppId: [''],
      oneSignalRestApiKey: [''],
      oneSignalGoogleSenderId: ['']
    });
    this.whitelabelFcmConfigForm = this.formBuilder.group({
      fcmSdkAdminFirebaseFile: ['', jsonValidator()],
      fcmAndroidJsonFile: ['', jsonValidator()],
      fcmIosPlistFile: ['', xmlValidator()],
      fcmWebPushFile: ['', jsonValidator()],
      fcmVapidKey: ['']
    });
    this.whitelabelStoreDetailsForm = this.formBuilder.group({
      applePackageName: [''],
      googlePackageName: [''],
      appleAppId: ['']
    });
    this.whitelabelEmailConfigForm = this.formBuilder.group({
      user: [''],
      password: [''],
      host: [''],
      ssl: [false],
      disabledMail: [false],
      port: [80]
    });
    this.oneSignalSetup = this.formBuilder.group({
      certificateId: [''],
      certificateFile: [null],
      firebaseId: [''],
      firebaseKey: ['']
    });
    this.whitelabelAccessPermissionForm = this.formBuilder.group({
      isPrivate: [false]
    });

    if (this.whitelabel) {
      this.setValuesToUpdate();
    }
    this.whitelabelDetailsForm
      .get('cnpj')
      ?.valueChanges?.pipe(
        filter(v => v !== null && v !== undefined),
        distinctUntilChanged(),
        map(value => {
          const cleanValue = value.replace(/\D/g, '');
          const limitedValue = cleanValue.substring(0, 14);
          return limitedValue ? formatCnpj(limitedValue) : '';
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(cnpj => {
        this.whitelabelDetailsForm.get('cnpj')?.setValue(cnpj, { emitEvent: false });
      });

    this.whitelabelDetailsForm
      .get('phone')
      ?.valueChanges?.pipe(
        filter(() => !this.whitelabel),
        filter(v => v),
        distinctUntilChanged(),
        map(value => formatPhone(value)),
        takeUntil(this.destroyed$)
      )
      .subscribe(phone => this.whitelabelDetailsForm.get('phone')?.setValue(phone, { emitEvent: false }));
  }

  ngOnInit(): void {
    this.whitelabel = this.route.snapshot.data?.whiteLabel;
    this.initForms();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private buildSmtpConfig(whitelabel: any) {
    if (this.smtpByNodeMailer) {
      const { user, password, host, port } = this.whitelabelEmailConfigForm.value;
      const nodemailerUri = `smtps://${user}:${password}@${host}:${port}`;
      whitelabel.emailjs = null;
      whitelabel.nodemailerUri = nodemailerUri;
    } else {
      whitelabel.emailjs = this.whitelabelEmailConfigForm.value;
      whitelabel.nodemailerUri = '';
    }
  }

  private buildWhitelabel(): UntypedFormGroup {
    this.whitelabelDetailsForm.get('welcomeMessage')?.disable();
    const details = { ...this.whitelabelDetailsForm.controls };
    const packages = { ...this.whitelabelStoreDetailsForm.controls };
    const permissionAccessKeys = { ...this.whitelabelAccessPermissionForm.controls };
    const cssVariables = this.whitelabelStylesForm;
    const integrationKeys = { ...this.whitelabelIntegrationKeysForm.controls };
    const fcmConfig = { ...this.whitelabelFcmConfigForm.controls };
    const emailjs = this.whitelabelEmailConfigForm;
    const whitelabelAppsKeys = { ...this.whitelabelAppsForm.controls };
    return new UntypedFormGroup({
      ...details,
      ...packages,
      ...integrationKeys,
      ...fcmConfig,
      ...permissionAccessKeys,
      ...whitelabelAppsKeys,
      cssVariables,
      emailjs
    });
  }

  private dirtyForms(): void {
    Object.values(this.whitelabelDetailsForm.controls).forEach(control => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
    Object.values(this.whitelabelStoreDetailsForm.controls).forEach(control => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
    Object.values(this.whitelabelStylesForm.controls).forEach(control => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
    Object.values(this.whitelabelIntegrationKeysForm.controls).forEach(control => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
    Object.values(this.whitelabelFcmConfigForm.controls).forEach(control => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
    Object.values(this.whitelabelEmailConfigForm.controls).forEach(control => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
    Object.values(this.whitelabelAccessPermissionForm.controls).forEach(control => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  private buildUploadWhiteLabelBase64ImagesDTOs(): UploadWhiteLabelBase64ImagesDTO[] {
    return Object.values(this.whitelabelImages)
      .filter(image => !!image.imageBase64 && !!image.imageFileName)
      .reduce((acc, currentValue) => {
        const imageToUpload: UploadWhiteLabelBase64ImagesDTO = {
          name: `${currentValue.imageFileName || ''}.png`,
          path: currentValue.imageFolder || '',
          base64: currentValue.imageBase64 || ''
        };

        acc.push(imageToUpload);

        if (currentValue.derivedImages?.length) {
          const derivedImagesToUpload: UploadWhiteLabelBase64ImagesDTO[] = currentValue.derivedImages
            .filter(derivedImage => !!derivedImage.imageBase64 && !!derivedImage.imageFileName)
            .map(derivedImage => ({
              name: `${derivedImage.imageFileName}.png`,
              path: imageToUpload.path ? `${imageToUpload.path}/${derivedImage.imageFolder || ''}` : derivedImage.imageFolder || '',
              base64: derivedImage.imageBase64 || ''
            }));

          acc.push(...derivedImagesToUpload);
        }

        return acc;
      }, [] as UploadWhiteLabelBase64ImagesDTO[]);
  }

  public save(): void {
    this.status = this.STATUS.LOADING;

    const whitelabel = this.buildWhitelabel();

    if (whitelabel.invalid || this.whitelabelImagesFormState.invalid) {
      this.dirtyForms();
      this.whitelabelImagesFormState.touched = true;

      this.notificationService.create('error', 'Campos inválidos', 'Existem campos inválidos!');
      this.status = this.STATUS.ERROR;

      return;
    }

    let newWhitelabel = whitelabel.value;
    this.buildSmtpConfig(newWhitelabel);

    newWhitelabel = {
      ...newWhitelabel,
      disabledMail: this.whitelabelEmailConfigForm.get('disabledMail')!.value,
      textReplacements: this.textReplacements,
      filesReplacements: this.generateFileReplacements(),
      ...this.generateResourceReplacements()
    };

    const subscription = this.whitelabel?._id
      ? this.whitelabelService.update(this.whitelabel._id, newWhitelabel).pipe(map(() => ({ _id: this.whitelabel._id })))
      : this.whitelabelService.create(newWhitelabel);

    const imagesToUpload = this.buildUploadWhiteLabelBase64ImagesDTOs();

    subscription
      .pipe(
        switchMap(whiteLabel =>
          imagesToUpload.length ? this.whitelabelService.uploadBase64Images(whiteLabel._id, imagesToUpload) : of(null)
        ),
        timeout(20_000)
      )
      .subscribe({
        next: () => {
          this.status = this.STATUS.SUCCESS;
          this.notificationService.create('success', 'WhiteLabel', 'Salvo com sucesso!');
          this.router.navigate(['home', 'whitelabels']);
        },
        error: err => {
          this.status = this.STATUS.ERROR;
          this.notificationService.create('error', 'Erro', err?.error?.message || 'Não foi possível cadastrar!');
        }
      });
  }

  public onIndexChange(event: number): void {
    this.index = event;
  }

  public copyLink(value: string = '') {
    this.clipboard.copy(value);
    this.notificationService.success('Link copiada!', '');
  }
  public onCnpjKeyPress(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;

    if (
      charCode === 8 ||
      charCode === 9 ||
      charCode === 27 ||
      charCode === 13 ||
      charCode === 46 ||
      charCode === 35 ||
      charCode === 36 ||
      (charCode >= 37 && charCode <= 40)
    ) {
      return;
    }

    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return;
    }
  }
  public onCnpjPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text');

    if (!pastedText) {
      return;
    }

    const cleanValue = pastedText.replace(/\D/g, '');

    const limitedValue = cleanValue.substring(0, 14);

    if (limitedValue) {
      const formattedValue = formatCnpj(limitedValue);
      this.whitelabelDetailsForm.get('cnpj')?.setValue(formattedValue);
    }
  }

  public handleCertificateUpload({ file }: NzUploadChangeParam): void {
    this.oneSignalSetup.get('certificateFile')?.setValue(file.originFileObj);
  }

  public async handleOneSignalConfig(): Promise<void> {
    this.oneSignalSetupStatus = this.STATUS.LOADING;
    if (this.whitelabelDetailsForm.invalid || this.oneSignalSetup.invalid) {
      this.notificationService.error('Ooops...', 'Existem valores inválidos', { nzDuration: 2000 }).onClick.subscribe(noop);
      this.oneSignalSetupStatus = this.STATUS.ERROR;
      return Promise.resolve();
    }
    const formValues: { certificateId: string; certificateFile: File; firebaseKey: string; firebaseId: string } = {
      ...this.oneSignalSetup.value
    };
    const name = this.whitelabelDetailsForm.get('companyName')?.value;
    const apns_p12: any = await getBase64FromFile(formValues.certificateFile);
    const data: OneSignalApp = {
      name,
      apns_p12,
      apns_p12_password: formValues.certificateId,
      android_gcm_sender_id: formValues.firebaseId,
      gcm_key: formValues.firebaseKey,
      organization_id: environment.ONE_SIGNAL_WHITELABEL_ORG_ID
    };
    this.oneSignalService.createApp(data).subscribe({
      next: ({ id, authKey }) => {
        const oneSignalAppId = id;
        const oneSignalRestApiKey = authKey;
        const oneSignalGoogleSenderId = formValues.firebaseId;
        this.whitelabelIntegrationKeysForm.setValue({ oneSignalAppId, oneSignalRestApiKey, oneSignalGoogleSenderId });
        this.whitelabelIntegrationKeysForm.markAsDirty();
        this.oneSignalSetupStatus = this.STATUS.SUCCESS;
        this.notificationService
          .success('App criado', 'O aplicativo foi criado no OneSignal', { nzDuration: 2000 })
          .onClick.subscribe(noop);
      },
      error: error => {
        this.oneSignalSetupStatus = this.STATUS.ERROR;
        this.notificationService
          .error('App não criado', 'Não foi possível criar o aplicativo', { nzDuration: 2000 })
          .onClick.subscribe(noop);
      }
    });
  }

  private generateFileReplacements(): { from: string; to: string }[] {
    const whiteLabelName = (this.whitelabelDetailsForm.get('name') as UntypedFormControl).value;
    const whiteLabelS3Url = `${environment.awsBucketWhiteLabelBaseUrl}/${whiteLabelName}`;
    return Object.keys(awsS3Paths).map(key => {
      const from = key;
      const s3Path = awsS3Paths[key];
      const to = `${whiteLabelS3Url}/${s3Path}`;
      return { from, to };
    });
  }

  private generateResourceReplacements(): ResourceReplacements {
    const whiteLabelName = (this.whitelabelDetailsForm.get('name') as UntypedFormControl).value;
    const whiteLabelS3Url = `${environment.awsBucketWhiteLabelBaseUrl}/${whiteLabelName}`;

    return {
      favIcon: `${whiteLabelS3Url}/favIcon.png`,
      logo: `${whiteLabelS3Url}/logo.png`,
      icon: `${whiteLabelS3Url}/icon.png`,
      splash: `${whiteLabelS3Url}/splash.png`,
      banner: `${whiteLabelS3Url}/logo.png`
    };
  }

  handleChangeWhitelabelImages(whitelabelImages: WhitelabelImages) {
    this.whitelabelImages = whitelabelImages;
  }

  handleChangeWhitelabelImagesFormState(formState: CreateWhitelabelIconAndImagesChangeFormState) {
    this.whitelabelImagesFormState = formState;
  }

  handleChangeWhiteLabelTextReplacements(textReplacements: WhiteLabelTextReplacement[]) {
    const validTextReplacements = textReplacements.filter(
      textReplacement => !!textReplacement.files.length && !!textReplacement.from && !!textReplacement.to
    );

    this.textReplacements = validTextReplacements;
  }

  toggleFcmFieldVisibility(fieldName: string): void {
    this.fcmFieldsVisibility[fieldName] = !this.fcmFieldsVisibility[fieldName];
  }
}
